/**
 * ============================================================================
 * BELLBOY RESIDENTIAL TECHNOLOGIES — ADMIN CONSOLE
 * ============================================================================
 * Single-file React app (App.js) backed by Supabase.
 *
 * ROLES
 *   superadmin — creates HR & intern accounts, sees everything, full activity log
 *   hr         — assigns tasks to interns, reviews submissions, creates interns
 *   intern     — sees only their own tasks, submits files/notes against them
 *
 * ----------------------------------------------------------------------------
 * SUPABASE SETUP (run once in the SQL editor)
 * ----------------------------------------------------------------------------
 *
 * -- 1. Profiles table (role lives here, keyed to auth.users)
 * create table profiles (
 *   id          uuid primary key references auth.users(id) on delete cascade,
 *   email       text not null,
 *   full_name   text not null,
 *   role        text not null check (role in ('superadmin','hr','intern')),
 *   created_by  uuid references profiles(id),
 *   created_at  timestamptz default now()
 * );
 *
 * -- 2. Tasks table
 * create table tasks (
 *   id           uuid primary key default gen_random_uuid(),
 *   title        text not null,
 *   description  text,
 *   assigned_to  uuid references profiles(id),
 *   assigned_by  uuid references profiles(id),
 *   status       text not null default 'pending'
 *                check (status in ('pending','in_progress','submitted','approved','rejected')),
 *   due_date     date,
 *   created_at   timestamptz default now()
 * );
 *
 * -- 3. Submissions table
 * create table submissions (
 *   id            uuid primary key default gen_random_uuid(),
 *   task_id       uuid references tasks(id) on delete cascade,
 *   intern_id     uuid references profiles(id),
 *   file_url      text,
 *   file_name     text,
 *   notes         text,
 *   status        text not null default 'pending'
 *                 check (status in ('pending','approved','rejected')),
 *   feedback      text,
 *   reviewed_by   uuid references profiles(id),
 *   reviewed_at   timestamptz,
 *   submitted_at  timestamptz default now()
 * );
 *
 * -- 4. Activity log table
 * create table activity_logs (
 *   id          uuid primary key default gen_random_uuid(),
 *   user_id     uuid references profiles(id),
 *   user_email  text,
 *   role        text,
 *   action      text not null,
 *   details     text,
 *   created_at  timestamptz default now()
 * );
 *
 * -- 5. Storage bucket for submitted files
 * insert into storage.buckets (id, name, public) values ('submissions', 'submissions', true);
 *
 * -- 6. Enable RLS + policies (tighten to taste; this is a reasonable starting point)
 * alter table profiles enable row level security;
 * alter table tasks enable row level security;
 * alter table submissions enable row level security;
 * alter table activity_logs enable row level security;
 *
 * create policy "read own profile or if staff" on profiles for select
 *   using (auth.uid() = id or exists (select 1 from profiles p where p.id = auth.uid() and p.role in ('superadmin','hr')));
 * create policy "staff manage profiles" on profiles for insert
 *   with check (exists (select 1 from profiles p where p.id = auth.uid() and p.role in ('superadmin','hr')));
 *
 * create policy "read relevant tasks" on tasks for select
 *   using (assigned_to = auth.uid() or exists (select 1 from profiles p where p.id = auth.uid() and p.role in ('superadmin','hr')));
 * create policy "staff create tasks" on tasks for insert
 *   with check (exists (select 1 from profiles p where p.id = auth.uid() and p.role in ('superadmin','hr')));
 * create policy "staff update tasks" on tasks for update
 *   using (exists (select 1 from profiles p where p.id = auth.uid() and p.role in ('superadmin','hr')));
 *
 * create policy "read relevant submissions" on submissions for select
 *   using (intern_id = auth.uid() or exists (select 1 from profiles p where p.id = auth.uid() and p.role in ('superadmin','hr')));
 * create policy "intern create own submission" on submissions for insert
 *   with check (intern_id = auth.uid());
 * create policy "staff update submissions" on submissions for update
 *   using (exists (select 1 from profiles p where p.id = auth.uid() and p.role in ('superadmin','hr')));
 *
 * create policy "read logs if staff" on activity_logs for select
 *   using (exists (select 1 from profiles p where p.id = auth.uid() and p.role in ('superadmin','hr')));
 * create policy "anyone logged in can write logs" on activity_logs for insert
 *   with check (auth.uid() is not null);
 *
 * ----------------------------------------------------------------------------
 * IMPORTANT SECURITY NOTE ON CREATING USERS
 * ----------------------------------------------------------------------------
 * Creating a user with a chosen email + password from the browser requires
 * calling Supabase Auth's signUp(). This app uses a SECOND, throwaway
 * Supabase client instance for that call so it doesn't hijack the admin's
 * own logged-in session. That's fine for getting this running today.
 *
 * For real production use, move user creation into a Supabase Edge Function
 * that uses the service_role key server-side and calls
 * supabase.auth.admin.createUser(). Never ship the service_role key to the
 * browser. Also turn OFF "Confirm email" under Auth settings (or the new
 * intern/HR user won't be able to log in until they click a confirmation
 * link you'd have to relay to them yourself).
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { createClient } from "@supabase/supabase-js";

// ---------------------------------------------------------------------------
// SUPABASE CONFIG — replace with your project values (or wire up env vars)
// ---------------------------------------------------------------------------
const SUPABASE_URL = "https://zbqctfrujyckndcboaik.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InpicWN0ZnJ1anlja25kY2JvYWlrIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODg1MTM2NTUsImV4cCI6MjEwNDA4OTY1NX0._TVGhWauRXWbnDIUaJ2nw1ooeCf7676JMhtfmkl_ZkA";

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// Secondary client used ONLY for admin-triggered signUp calls, so creating a
// new user doesn't swap out the currently logged-in admin's session.
function makeThrowawayClient() {
  return createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: { persistSession: false, autoRefreshToken: false },
  });
}

// ---------------------------------------------------------------------------
// STYLES — self-contained, no external CSS framework required
// ---------------------------------------------------------------------------
const GlobalStyles = () => (
  <style>{`
    :root {
      --ink: #14213d;
      --ink-soft: #3b4863;
      --paper: #f7f5f0;
      --panel: #ffffff;
      --line: #e4e0d6;
      --accent: #1f7a6c;
      --accent-dark: #16594e;
      --amber: #b8752f;
      --red: #a63e3e;
      --radius: 10px;
    }
    * { box-sizing: border-box; }
    body, html, #root { height: 100%; margin: 0; }
    .bb-app {
      font-family: -apple-system, "Segoe UI", Inter, Roboto, sans-serif;
      background: var(--paper);
      color: var(--ink);
      min-height: 100vh;
    }
    .bb-login {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(160deg, var(--ink) 0%, #1c3050 55%, var(--ink) 100%);
    }
    .bb-login-card {
      background: var(--panel);
      width: 380px;
      max-width: 90vw;
      padding: 40px 36px;
      border-radius: var(--radius);
      box-shadow: 0 20px 60px rgba(0,0,0,0.35);
    }
    .bb-brand {
      font-size: 13px;
      letter-spacing: 0.02em;
      color: var(--accent);
      font-weight: 600;
      margin-bottom: 4px;
    }
    .bb-login-title {
      font-size: 24px;
      font-weight: 700;
      margin: 0 0 28px 0;
      color: var(--ink);
    }
    .bb-field { margin-bottom: 16px; }
    .bb-field label {
      display: block;
      font-size: 13px;
      color: var(--ink-soft);
      margin-bottom: 6px;
    }
    .bb-field input, .bb-field select, .bb-field textarea {
      width: 100%;
      padding: 10px 12px;
      border: 1px solid var(--line);
      border-radius: 8px;
      font-size: 14px;
      background: #fff;
      color: var(--ink);
    }
    .bb-field input:focus, .bb-field select:focus, .bb-field textarea:focus {
      outline: 2px solid var(--accent);
      outline-offset: 1px;
      border-color: var(--accent);
    }
    .bb-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      background: var(--accent);
      color: #fff;
      border: none;
      padding: 10px 18px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
    }
    .bb-btn:hover { background: var(--accent-dark); }
    .bb-btn:disabled { opacity: 0.55; cursor: not-allowed; }
    .bb-btn-ghost {
      background: transparent;
      color: var(--ink-soft);
      border: 1px solid var(--line);
    }
    .bb-btn-ghost:hover { background: #f0ede4; }
    .bb-btn-danger { background: var(--red); }
    .bb-btn-danger:hover { background: #832f2f; }
    .bb-error {
      background: #fbeaea;
      color: var(--red);
      padding: 10px 12px;
      border-radius: 8px;
      font-size: 13px;
      margin-bottom: 16px;
    }
    .bb-success {
      background: #e8f3ef;
      color: var(--accent-dark);
      padding: 10px 12px;
      border-radius: 8px;
      font-size: 13px;
      margin-bottom: 16px;
    }
    .bb-shell { display: flex; min-height: 100vh; }
    .bb-sidebar {
      width: 232px;
      background: var(--ink);
      color: #cfd6e4;
      padding: 24px 16px;
      display: flex;
      flex-direction: column;
    }
    .bb-sidebar-brand { color: #fff; font-weight: 700; font-size: 16px; margin-bottom: 2px; }
    .bb-sidebar-role {
      font-size: 12px;
      color: var(--accent);
      text-transform: capitalize;
      margin-bottom: 28px;
      font-weight: 600;
    }
    .bb-nav-item {
      display: block;
      width: 100%;
      text-align: left;
      background: transparent;
      border: none;
      color: #cfd6e4;
      padding: 10px 12px;
      border-radius: 8px;
      font-size: 14px;
      cursor: pointer;
      margin-bottom: 4px;
    }
    .bb-nav-item:hover { background: rgba(255,255,255,0.06); }
    .bb-nav-item.active { background: var(--accent); color: #fff; }
    .bb-sidebar-footer { margin-top: auto; }
    .bb-user-chip { font-size: 13px; color: #fff; margin-bottom: 10px; }
    .bb-user-chip small { display: block; color: #9aa6bd; font-weight: 400; }
    .bb-main { flex: 1; padding: 32px 40px; overflow-x: auto; }
    .bb-page-title { font-size: 22px; font-weight: 700; margin: 0 0 4px 0; }
    .bb-page-sub { color: var(--ink-soft); font-size: 14px; margin: 0 0 24px 0; }
    .bb-panel {
      background: var(--panel);
      border: 1px solid var(--line);
      border-radius: var(--radius);
      padding: 24px;
      margin-bottom: 24px;
    }
    .bb-panel h3 { margin: 0 0 16px 0; font-size: 16px; }
    .bb-row { display: flex; gap: 16px; flex-wrap: wrap; }
    .bb-row > div { flex: 1; min-width: 200px; }
    table.bb-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
    table.bb-table th {
      text-align: left;
      padding: 10px 12px;
      border-bottom: 2px solid var(--line);
      color: var(--ink-soft);
      font-weight: 600;
      font-size: 12.5px;
    }
    table.bb-table td {
      padding: 10px 12px;
      border-bottom: 1px solid var(--line);
      vertical-align: top;
    }
    table.bb-table tr:hover td { background: #faf9f5; }
    .bb-badge {
      display: inline-block;
      padding: 3px 10px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 600;
    }
    .bb-badge.pending { background: #f5e9d8; color: var(--amber); }
    .bb-badge.in_progress { background: #e3ecf8; color: #2b5aa0; }
    .bb-badge.submitted { background: #ece3f8; color: #6a3fa0; }
    .bb-badge.approved { background: #e2f3ea; color: var(--accent-dark); }
    .bb-badge.rejected { background: #fbeaea; color: var(--red); }
    .bb-badge.superadmin { background: #14213d; color: #fff; }
    .bb-badge.hr { background: #e3ecf8; color: #2b5aa0; }
    .bb-badge.intern { background: #e2f3ea; color: var(--accent-dark); }
    .bb-tabs { display: flex; gap: 6px; margin-bottom: 20px; border-bottom: 1px solid var(--line); }
    .bb-tab {
      background: none;
      border: none;
      padding: 10px 14px;
      font-size: 14px;
      color: var(--ink-soft);
      cursor: pointer;
      border-bottom: 2px solid transparent;
      margin-bottom: -1px;
    }
    .bb-tab.active { color: var(--accent-dark); border-bottom-color: var(--accent); font-weight: 600; }
    .bb-empty { color: var(--ink-soft); font-size: 14px; padding: 20px 0; text-align: center; }
    .bb-modal-overlay {
      position: fixed; inset: 0; background: rgba(20,33,61,0.45);
      display: flex; align-items: center; justify-content: center; z-index: 50;
      padding: 20px;
    }
    .bb-modal {
      background: #fff; border-radius: var(--radius); padding: 28px;
      width: 460px; max-width: 100%; max-height: 90vh; overflow-y: auto;
    }
    .bb-modal h3 { margin-top: 0; }
    .bb-link { color: var(--accent-dark); font-weight: 600; text-decoration: none; }
    .bb-link:hover { text-decoration: underline; }
    .bb-muted { color: var(--ink-soft); font-size: 13px; }
    .bb-topline { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
  `}</style>
);

// ---------------------------------------------------------------------------
// SMALL HELPERS
// ---------------------------------------------------------------------------
function Badge({ value }) {
  const label = (value || "").replace("_", " ");
  return <span className={`bb-badge ${value}`}>{label}</span>;
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function fmtDateTime(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

async function logActivity(profile, action, details) {
  if (!profile) return;
  await supabase.from("activity_logs").insert({
    user_id: profile.id,
    user_email: profile.email,
    role: profile.role,
    action,
    details: details || null,
  });
}

// ---------------------------------------------------------------------------
// LOGIN SCREEN
// ---------------------------------------------------------------------------
function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });
      if (signInError) throw signInError;
    } catch (err) {
      setError(err.message || "Unable to sign in. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bb-login">
      <div className="bb-login-card">
        <div className="bb-brand">BELLBOY RESIDENTIAL TECHNOLOGIES</div>
        <h1 className="bb-login-title">Admin Console</h1>
        {error && <div className="bb-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="bb-field">
            <label>Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@bellboy.com" />
          </div>
          <div className="bb-field">
            <label>Password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          </div>
          <button className="bb-btn" style={{ width: "100%" }} disabled={loading} type="submit">
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SHARED: sidebar shell
// ---------------------------------------------------------------------------
function Shell({ profile, tabs, activeTab, setActiveTab, children }) {
  const handleLogout = async () => {
    await logActivity(profile, "logged out");
    await supabase.auth.signOut();
  };

  return (
    <div className="bb-shell">
      <aside className="bb-sidebar">
        <div className="bb-sidebar-brand">Bellboy</div>
        <div className="bb-sidebar-role">{profile.role} console</div>
        {tabs.map((t) => (
          <button
            key={t.key}
            className={`bb-nav-item ${activeTab === t.key ? "active" : ""}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
        <div className="bb-sidebar-footer">
          <div className="bb-user-chip">
            {profile.full_name}
            <small>{profile.email}</small>
          </div>
          <button className="bb-nav-item" onClick={handleLogout}>Log out</button>
        </div>
      </aside>
      <main className="bb-main">{children}</main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SHARED: create-user form (used by superadmin for hr+intern, hr for intern only)
// ---------------------------------------------------------------------------
function CreateUserPanel({ profile, allowedRoles, onCreated }) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(allowedRoles[0]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const throwaway = makeThrowawayClient();
      const cleanEmail = email.trim().toLowerCase();
      const { data, error: signUpError } = await throwaway.auth.signUp({
        email: cleanEmail,
        password,
        options: { data: { full_name: fullName.trim(), role } },
      });
      if (signUpError) throw signUpError;
      const newUserId = data?.user?.id;
      if (!newUserId) throw new Error("Could not create the account. Check your Supabase email confirmation settings.");

      // The database trigger creates every new account as an intern. Staff can
      // then promote the new profile to the role selected in this form.
      const { data: existingProfile, error: profileLookupError } = await supabase
        .from("profiles").select("id").eq("id", newUserId).maybeSingle();
      if (profileLookupError) throw profileLookupError;
      if (existingProfile) {
        const { error: promoteError } = await supabase.from("profiles")
          .update({ email: cleanEmail, full_name: fullName.trim(), role, created_by: profile.id })
          .eq("id", newUserId);
        if (promoteError) throw promoteError;
      } else {
        const { error: profileError } = await supabase.from("profiles").insert({
          id: newUserId,
          email: cleanEmail,
          full_name: fullName.trim(),
          role,
          created_by: profile.id,
        });
        if (profileError) throw profileError;
      }

      await logActivity(profile, "created user", `${role} — ${email}`);
      setSuccess(`${role.toUpperCase()} account created for ${email}.`);
      setFullName("");
      setEmail("");
      setPassword("");
      onCreated && onCreated();
    } catch (err) {
      setError(err.message || "Something went wrong creating that account.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bb-panel">
      <h3>Create a new account</h3>
      {error && <div className="bb-error">{error}</div>}
      {success && <div className="bb-success">{success}</div>}
      <form onSubmit={handleCreate}>
        <div className="bb-row">
          <div className="bb-field">
            <label>Full name</label>
            <input required value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>
          <div className="bb-field">
            <label>Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              {allowedRoles.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="bb-row">
          <div className="bb-field">
            <label>Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="bb-field">
            <label>Temporary password</label>
            <input type="text" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="min 6 characters" />
          </div>
        </div>
        <button className="bb-btn" disabled={loading} type="submit">
          {loading ? "Creating…" : "Create account"}
        </button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SHARED: users table
// ---------------------------------------------------------------------------
function UsersTable({ users, roleFilter }) {
  const filtered = roleFilter ? users.filter((u) => roleFilter.includes(u.role)) : users;
  if (!filtered.length) return <div className="bb-empty">No accounts yet.</div>;
  return (
    <table className="bb-table">
      <thead>
        <tr><th>Name</th><th>Email</th><th>Role</th><th>Created</th></tr>
      </thead>
      <tbody>
        {filtered.map((u) => (
          <tr key={u.id}>
            <td>{u.full_name}</td>
            <td>{u.email}</td>
            <td><Badge value={u.role} /></td>
            <td>{fmtDate(u.created_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// SUPERADMIN DASHBOARD
// ---------------------------------------------------------------------------
function SuperAdminDashboard({ profile }) {
  const [activeTab, setActiveTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const [{ data: u }, { data: t }, { data: l }] = await Promise.all([
      supabase.from("profiles").select("*").order("created_at", { ascending: false }),
      supabase.from("tasks").select("*, assignee:assigned_to(full_name,email), assigner:assigned_by(full_name)").order("created_at", { ascending: false }),
      supabase.from("activity_logs").select("*").order("created_at", { ascending: false }).limit(200),
    ]);
    setUsers(u || []);
    setTasks(t || []);
    setLogs(l || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const tabs = [
    { key: "users", label: "Users" },
    { key: "tasks", label: "All tasks" },
    { key: "logs", label: "Activity log" },
  ];

  return (
    <Shell profile={profile} tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab}>
      <div className="bb-topline">
        <div>
          <h1 className="bb-page-title">
            {activeTab === "users" && "Users"}
            {activeTab === "tasks" && "All tasks"}
            {activeTab === "logs" && "Activity log"}
          </h1>
          <p className="bb-page-sub">
            {activeTab === "users" && "Create HR and intern accounts, and see everyone on the roster."}
            {activeTab === "tasks" && "Every task across the org, regardless of who assigned it."}
            {activeTab === "logs" && "A record of who did what, most recent first."}
          </p>
        </div>
      </div>

      {activeTab === "users" && (
        <>
          <CreateUserPanel profile={profile} allowedRoles={["hr", "intern"]} onCreated={loadAll} />
          <div className="bb-panel">
            <h3>All accounts</h3>
            {loading ? <div className="bb-empty">Loading…</div> : <UsersTable users={users} />}
          </div>
        </>
      )}

      {activeTab === "tasks" && (
        <div className="bb-panel">
          {loading ? (
            <div className="bb-empty">Loading…</div>
          ) : tasks.length === 0 ? (
            <div className="bb-empty">No tasks yet.</div>
          ) : (
            <table className="bb-table">
              <thead>
                <tr><th>Title</th><th>Assigned to</th><th>Assigned by</th><th>Due</th><th>Status</th></tr>
              </thead>
              <tbody>
                {tasks.map((t) => (
                  <tr key={t.id}>
                    <td>{t.title}</td>
                    <td>{t.assignee?.full_name || "—"}</td>
                    <td>{t.assigner?.full_name || "—"}</td>
                    <td>{fmtDate(t.due_date)}</td>
                    <td><Badge value={t.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === "logs" && (
        <div className="bb-panel">
          {loading ? (
            <div className="bb-empty">Loading…</div>
          ) : logs.length === 0 ? (
            <div className="bb-empty">No activity recorded yet.</div>
          ) : (
            <table className="bb-table">
              <thead>
                <tr><th>When</th><th>Who</th><th>Role</th><th>Action</th><th>Details</th></tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id}>
                    <td>{fmtDateTime(l.created_at)}</td>
                    <td>{l.user_email}</td>
                    <td><Badge value={l.role} /></td>
                    <td>{l.action}</td>
                    <td className="bb-muted">{l.details || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// HR DASHBOARD
// ---------------------------------------------------------------------------
function AssignTaskPanel({ profile, interns, onAssigned }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleAssign = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!assignedTo) { setError("Pick an intern to assign this to."); return; }
    setLoading(true);
    const { error: insertError } = await supabase.from("tasks").insert({
      title,
      description,
      assigned_to: assignedTo,
      assigned_by: profile.id,
      due_date: dueDate || null,
      status: "pending",
    });
    setLoading(false);
    if (insertError) { setError(insertError.message); return; }
    const intern = interns.find((i) => i.id === assignedTo);
    await logActivity(profile, "assigned task", `"${title}" → ${intern?.full_name || assignedTo}`);
    setSuccess("Task assigned.");
    setTitle(""); setDescription(""); setAssignedTo(""); setDueDate("");
    onAssigned && onAssigned();
  };

  return (
    <div className="bb-panel">
      <h3>Assign a task</h3>
      {error && <div className="bb-error">{error}</div>}
      {success && <div className="bb-success">{success}</div>}
      <form onSubmit={handleAssign}>
        <div className="bb-field">
          <label>Title</label>
          <input required value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="bb-field">
          <label>Description</label>
          <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="bb-row">
          <div className="bb-field">
            <label>Assign to</label>
            <select required value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
              <option value="">Select an intern…</option>
              {interns.map((i) => (
                <option key={i.id} value={i.id}>{i.full_name} ({i.email})</option>
              ))}
            </select>
          </div>
          <div className="bb-field">
            <label>Due date</label>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </div>
        </div>
        <button className="bb-btn" disabled={loading} type="submit">
          {loading ? "Assigning…" : "Assign task"}
        </button>
      </form>
    </div>
  );
}

function ReviewModal({ submission, profile, onClose, onReviewed }) {
  const [feedback, setFeedback] = useState(submission.feedback || "");
  const [loading, setLoading] = useState(false);

  const decide = async (decision) => {
    setLoading(true);
    await supabase.from("submissions").update({
      status: decision,
      feedback,
      reviewed_by: profile.id,
      reviewed_at: new Date().toISOString(),
    }).eq("id", submission.id);
    await supabase.from("tasks").update({ status: decision }).eq("id", submission.task_id);
    await logActivity(profile, `${decision} submission`, submission.task?.title || submission.task_id);
    setLoading(false);
    onReviewed();
    onClose();
  };

  return (
    <div className="bb-modal-overlay" onClick={onClose}>
      <div className="bb-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Review submission</h3>
        <p className="bb-muted">
          <strong>{submission.task?.title}</strong><br />
          From {submission.intern?.full_name} · {fmtDateTime(submission.submitted_at)}
        </p>
        {submission.notes && (
          <div className="bb-field">
            <label>Intern's notes</label>
            <div style={{ fontSize: 14 }}>{submission.notes}</div>
          </div>
        )}
        {submission.file_url && (
          <div className="bb-field">
            <label>Submitted file</label>
            <a className="bb-link" href={submission.file_url} target="_blank" rel="noreferrer">
              {submission.file_name || "Open file"}
            </a>
          </div>
        )}
        <div className="bb-field">
          <label>Feedback (optional)</label>
          <textarea rows={3} value={feedback} onChange={(e) => setFeedback(e.target.value)} />
        </div>
        <div className="bb-row">
          <button className="bb-btn" disabled={loading} onClick={() => decide("approved")}>Approve</button>
          <button className="bb-btn bb-btn-danger" disabled={loading} onClick={() => decide("rejected")}>Reject</button>
          <button className="bb-btn bb-btn-ghost" disabled={loading} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function HRDashboard({ profile }) {
  const [activeTab, setActiveTab] = useState("assign");
  const [interns, setInterns] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [reviewing, setReviewing] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const [{ data: i }, { data: s }] = await Promise.all([
      supabase.from("profiles").select("*").eq("role", "intern").order("full_name"),
      supabase.from("submissions")
        .select("*, task:task_id(title), intern:intern_id(full_name,email)")
        .order("submitted_at", { ascending: false }),
    ]);
    setInterns(i || []);
    setSubmissions(s || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const tabs = [
    { key: "assign", label: "Assign task" },
    { key: "review", label: "Review submissions" },
    { key: "interns", label: "Interns" },
  ];

  const pending = submissions.filter((s) => s.status === "pending");
  const reviewed = submissions.filter((s) => s.status !== "pending");

  return (
    <Shell profile={profile} tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab}>
      <h1 className="bb-page-title">
        {activeTab === "assign" && "Assign task"}
        {activeTab === "review" && "Review submissions"}
        {activeTab === "interns" && "Interns"}
      </h1>
      <p className="bb-page-sub">
        {activeTab === "assign" && "Give an intern something to work on."}
        {activeTab === "review" && "Approve or send back what interns have submitted."}
        {activeTab === "interns" && "Everyone currently on the intern roster, and adding new ones."}
      </p>

      {activeTab === "assign" && (
        <AssignTaskPanel profile={profile} interns={interns} onAssigned={loadAll} />
      )}

      {activeTab === "review" && (
        <>
          <div className="bb-panel">
            <h3>Pending review ({pending.length})</h3>
            {loading ? (
              <div className="bb-empty">Loading…</div>
            ) : pending.length === 0 ? (
              <div className="bb-empty">Nothing waiting on you right now.</div>
            ) : (
              <table className="bb-table">
                <thead><tr><th>Task</th><th>Intern</th><th>Submitted</th><th></th></tr></thead>
                <tbody>
                  {pending.map((s) => (
                    <tr key={s.id}>
                      <td>{s.task?.title}</td>
                      <td>{s.intern?.full_name}</td>
                      <td>{fmtDateTime(s.submitted_at)}</td>
                      <td><button className="bb-btn" onClick={() => setReviewing(s)}>Review</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="bb-panel">
            <h3>Already reviewed</h3>
            {reviewed.length === 0 ? (
              <div className="bb-empty">Nothing reviewed yet.</div>
            ) : (
              <table className="bb-table">
                <thead><tr><th>Task</th><th>Intern</th><th>Status</th><th>Feedback</th></tr></thead>
                <tbody>
                  {reviewed.map((s) => (
                    <tr key={s.id}>
                      <td>{s.task?.title}</td>
                      <td>{s.intern?.full_name}</td>
                      <td><Badge value={s.status} /></td>
                      <td className="bb-muted">{s.feedback || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {activeTab === "interns" && (
        <>
          <CreateUserPanel profile={profile} allowedRoles={["intern"]} onCreated={loadAll} />
          <div className="bb-panel">
            <h3>Current interns</h3>
            {loading ? <div className="bb-empty">Loading…</div> : <UsersTable users={interns} />}
          </div>
        </>
      )}

      {reviewing && (
        <ReviewModal
          submission={reviewing}
          profile={profile}
          onClose={() => setReviewing(null)}
          onReviewed={loadAll}
        />
      )}
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// INTERN DASHBOARD
// ---------------------------------------------------------------------------
function SubmitModal({ task, profile, onClose, onSubmitted }) {
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      let fileUrl = null;
      let fileName = null;
      if (file) {
        const path = `${profile.id}/${task.id}/${Date.now()}-${file.name}`;
        const { error: uploadError } = await supabase.storage.from("submissions").upload(path, file);
        if (uploadError) throw uploadError;
        const { data: publicUrlData } = supabase.storage.from("submissions").getPublicUrl(path);
        fileUrl = publicUrlData.publicUrl;
        fileName = file.name;
      }
      const { error: insertError } = await supabase.from("submissions").insert({
        task_id: task.id,
        intern_id: profile.id,
        file_url: fileUrl,
        file_name: fileName,
        notes,
        status: "pending",
      });
      if (insertError) throw insertError;

      await supabase.from("tasks").update({ status: "submitted" }).eq("id", task.id);
      await logActivity(profile, "submitted task", task.title);
      onSubmitted();
      onClose();
    } catch (err) {
      setError(err.message || "Couldn't submit that — try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bb-modal-overlay" onClick={onClose}>
      <div className="bb-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Submit: {task.title}</h3>
        {error && <div className="bb-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="bb-field">
            <label>File (PDF, image, or any document)</label>
            <input type="file" onChange={(e) => setFile(e.target.files[0])} />
          </div>
          <div className="bb-field">
            <label>Notes</label>
            <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything HR should know about this submission…" />
          </div>
          <div className="bb-row">
            <button className="bb-btn" disabled={loading} type="submit">{loading ? "Submitting…" : "Submit"}</button>
            <button type="button" className="bb-btn bb-btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function InternDashboard({ profile }) {
  const [activeTab, setActiveTab] = useState("tasks");
  const [tasks, setTasks] = useState([]);
  const [submitting, setSubmitting] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("tasks")
      .select("*, assigner:assigned_by(full_name)")
      .eq("assigned_to", profile.id)
      .order("created_at", { ascending: false });
    setTasks(data || []);
    setLoading(false);
  }, [profile.id]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const tabs = [{ key: "tasks", label: "My tasks" }];
  const open = tasks.filter((t) => ["pending", "in_progress", "rejected"].includes(t.status));
  const done = tasks.filter((t) => ["submitted", "approved"].includes(t.status));

  return (
    <Shell profile={profile} tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab}>
      <h1 className="bb-page-title">My tasks</h1>
      <p className="bb-page-sub">Everything assigned to you, and where it stands.</p>

      <div className="bb-panel">
        <h3>Needs your attention ({open.length})</h3>
        {loading ? (
          <div className="bb-empty">Loading…</div>
        ) : open.length === 0 ? (
          <div className="bb-empty">Nothing outstanding — nice.</div>
        ) : (
          <table className="bb-table">
            <thead><tr><th>Title</th><th>Assigned by</th><th>Due</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {open.map((t) => (
                <tr key={t.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{t.title}</div>
                    <div className="bb-muted">{t.description}</div>
                  </td>
                  <td>{t.assigner?.full_name || "—"}</td>
                  <td>{fmtDate(t.due_date)}</td>
                  <td><Badge value={t.status} /></td>
                  <td><button className="bb-btn" onClick={() => setSubmitting(t)}>Submit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bb-panel">
        <h3>Submitted / closed</h3>
        {done.length === 0 ? (
          <div className="bb-empty">Nothing here yet.</div>
        ) : (
          <table className="bb-table">
            <thead><tr><th>Title</th><th>Status</th></tr></thead>
            <tbody>
              {done.map((t) => (
                <tr key={t.id}>
                  <td>{t.title}</td>
                  <td><Badge value={t.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {submitting && (
        <SubmitModal
          task={submitting}
          profile={profile}
          onClose={() => setSubmitting(null)}
          onSubmitted={loadTasks}
        />
      )}
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// ROOT APP — auth wiring + role routing
// ---------------------------------------------------------------------------
function ProfileError({ message, onLogout }) {
  return (
    <div className="bb-login">
      <div className="bb-login-card">
        <div className="bb-brand">BELLBOY RESIDENTIAL TECHNOLOGIES</div>
        <h1 className="bb-login-title">Profile setup required</h1>
        <p className="bb-muted">
          Your Supabase login succeeded, but the matching row in <code>profiles</code> could not be loaded.
        </p>
        <div className="bb-error" style={{ whiteSpace: "pre-wrap" }}>{message}</div>
        <p className="bb-muted">
          Run the supplied Supabase migration once, then sign out and sign in again.
        </p>
        <button className="bb-btn bb-btn-ghost" onClick={onLogout}>Log out</button>
      </div>
    </div>
  );
}

async function loadOrCreateProfile(user) {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", user.id)
    .maybeSingle();

  if (error) throw new Error(`Could not read profiles: ${error.message}`);
  if (data) return data;

  // This is a safe recovery path for accounts created before the trigger was
  // installed. RLS must allow a user to insert only their own profile.
  const fallbackProfile = {
    id: user.id,
    email: (user.email || "").trim().toLowerCase(),
    full_name: (user.user_metadata?.full_name || user.email?.split("@")[0] || "User").trim(),
    role: "intern",
  };
  const { data: created, error: createError } = await supabase
    .from("profiles")
    .insert(fallbackProfile)
    .select("*")
    .single();

  if (createError) {
    throw new Error(
      `No profile exists for this login (${user.id}). ` +
      `Run bellboy_profiles_registration.sql in Supabase SQL Editor, then try again. ` +
      `Database error: ${createError.message}`
    );
  }
  return created;
}

export default function App() {
  const [session, setSession] = useState(undefined);
  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);

  const refreshProfile = useCallback(async (currentSession) => {
    if (!currentSession?.user) {
      setProfile(null);
      setProfileError("");
      return;
    }
    setProfileLoading(true);
    setProfileError("");
    try {
      const nextProfile = await loadOrCreateProfile(currentSession.user);
      setProfile(nextProfile);
      await logActivity(nextProfile, "logged in");
    } catch (err) {
      setProfile(null);
      setProfileError(err.message || "Could not load your profile.");
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data, error }) => {
      if (!active) return;
      if (error) setProfileError(error.message);
      setSession(data?.session || null);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!active) return;
      setSession(nextSession);
    });
    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    refreshProfile(session);
  }, [session, refreshProfile]);

  const content = useMemo(() => {
    if (session === undefined || (session && profileLoading)) {
      return <div className="bb-login"><div style={{ color: "#fff" }}>Loading…</div></div>;
    }
    if (!session) return <Login />;
    if (profileError) {
      return <ProfileError message={profileError} onLogout={() => supabase.auth.signOut()} />;
    }
    if (!profile) {
      return <div className="bb-login"><div style={{ color: "#fff" }}>Loading profile…</div></div>;
    }
    if (profile.role === "superadmin") return <SuperAdminDashboard profile={profile} />;
    if (profile.role === "hr") return <HRDashboard profile={profile} />;
    if (profile.role === "intern") return <InternDashboard profile={profile} />;
    return <ProfileError message={`Unsupported role "${profile.role}" in profiles. Use superadmin, hr, or intern.`} onLogout={() => supabase.auth.signOut()} />;
  }, [session, profile, profileError, profileLoading]);

  return (
    <div className="bb-app">
      <GlobalStyles />
      {content}
    </div>
  );
}
